/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * AcknowledgementDocSelection — Sponsor/Site ack document template picker.
 * Migrated from: lego/page/eos/transfer-media/acknowledgement-doc-selection/AcknowledgementDocSelection.jsx
 */

import { useEffect, useState, useRef } from 'react'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import { Select } from '../../../../components/Select'
import AckDocUploadModal from '../AckDocUploadModal'
import type {
  AcknowledgementDocSelectionProps,
  SelectedDocTypes,
  AckDocTemplate,
  AckDocTemplateListResponse
} from '../types'
import type { OptionType } from '../../../../components/Select'
import type { SingleValue } from 'react-select'

const AcknowledgementDocSelection: React.FC<AcknowledgementDocSelectionProps> = ({
  handleAckDocUpdate,
  contextId,
  isLegacyStudy,
  isReview
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { auth } = context.service
  const { showModal } = useModal()
  const [ackDocList, setAckDocList] = useState<AckDocTemplate[]>([])
  const [isDataFetched, setIsDataFetched] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [selectedDocTypes, setSelectedDocTypes] = useState<SelectedDocTypes>({
    eosStudyId: 0,
    siteAckTemplateFsiId: 0,
    siteAckTemplateFsiName: '',
    sponsorAckTemplateFsiId: 0,
    sponsorAckTemplateFsiName: ''
  })
  const selectedDataRef = useRef({
    siteAckTemplateFsiId: 0,
    sponsorAckTemplateFsiId: 0
  })

  useEffect(() => {
    const adminRole = context.constant.user?.roles?.ADMIN ?? 'ADMIN'
    setIsAdmin(auth.getRole() === adminRole)
  }, [])

  useEffect(() => {
    getAckDocTemplateList(true)
  }, [contextId])

  useEffect(() => {
    handleAckDocUpdate(selectedDocTypes)
  }, [selectedDocTypes])

  const getAckDocTemplateList = (shouldUpdateSelection = true) => {
    if (contextId !== undefined) {
      context.api.eos.getAckDocTemplateList({ ftpContextId: contextId }).then((_data) => {
        const data = _data as AckDocTemplateListResponse
        if (data?.ackDocTemplates?.length) {
          setAckDocList(data.ackDocTemplates)
          if (shouldUpdateSelection) {
            let sponsorId = data.sponsorAckDocTemplateId
            let siteId = data.siteAckDocTemplateId

            if (sponsorId === 0) {
              sponsorId = data.ackDocTemplates.filter((item: AckDocTemplate) => item.id !== siteId)[0]?.id ?? 0
            }
            if (siteId === 0) {
              siteId = data.ackDocTemplates.filter((item: AckDocTemplate) => item.id !== sponsorId)[0]?.id ?? 0
            }

            setSelectedDocTypes((oldData) => ({
              ...oldData,
              siteAckTemplateFsiId: siteId,
              siteAckTemplateFsiName:
                data.ackDocTemplates.find((item: AckDocTemplate) => item.id === siteId)?.name ?? '',
              sponsorAckTemplateFsiId: sponsorId,
              sponsorAckTemplateFsiName:
                data.ackDocTemplates.find((item: AckDocTemplate) => item.id === sponsorId)?.name ?? ''
            }))
            selectedDataRef.current = {
              siteAckTemplateFsiId: siteId,
              sponsorAckTemplateFsiId: sponsorId
            }
          }
        }
        setIsDataFetched(true)
      })
    }
  }

  const getOptionsData = (type: 'SPONSOR' | 'SITE'): OptionType[] => {
    return ackDocList.length
      ? ackDocList
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) =>
            type === 'SPONSOR'
              ? item.value !== selectedDataRef.current.siteAckTemplateFsiId
              : item.value !== selectedDataRef.current.sponsorAckTemplateFsiId
          )
      : []
  }

  const handleDocUploadSuccess = () => {
    getAckDocTemplateList(false)
  }

  const handleAddNewAckDoc = () => {
    showModal(<AckDocUploadModal onDocUploadSuccess={handleDocUploadSuccess} />, { keepParentModalsOpen: true })
  }

  return (
    <div className="ack-doc-dropdown-wrapper">
      <label className="control-label title-wrapper">{translate('ACK_TEMPLATES')}</label>
      <div className="ack-doc-dropdown-container">
        <div className="ack-doc-dropdown">
          <label className="control-label">{translate('SPONSOR_TEMPLATES')}</label>
          <Select
            name={translate('SPONSOR_TEMPLATES')}
            value={selectedDocTypes.sponsorAckTemplateFsiId}
            options={getOptionsData('SPONSOR')}
            placeholder={translate('SELECT_FILE')}
            onChange={(option) => {
              const selected = option as SingleValue<OptionType>
              if (selected) {
                selectedDataRef.current.sponsorAckTemplateFsiId = selected.value
                setSelectedDocTypes((data) => ({
                  ...data,
                  sponsorAckTemplateFsiId: selected.value,
                  sponsorAckTemplateFsiName: selected.label
                }))
              }
            }}
            isClearable={false}
            searchable
            hideSelectedOptions
            isDisabled={isLegacyStudy || isReview}
          />
          {isDataFetched && !selectedDataRef.current.sponsorAckTemplateFsiId && (
            <div className="error justify-content-start">{translate('REQUIRED_PROPERTIES')}</div>
          )}
        </div>
        <div className="ack-doc-dropdown">
          <label className="control-label">{translate('SITE_TEMPLATES')}</label>
          <Select
            name={translate('SITE_TEMPLATES')}
            value={selectedDocTypes.siteAckTemplateFsiId}
            options={getOptionsData('SITE')}
            placeholder={translate('SELECT_FILE')}
            onChange={(option) => {
              const selected = option as SingleValue<OptionType>
              if (selected) {
                selectedDataRef.current.siteAckTemplateFsiId = selected.value
                setSelectedDocTypes((data) => ({
                  ...data,
                  siteAckTemplateFsiId: selected.value,
                  siteAckTemplateFsiName: selected.label
                }))
              }
            }}
            isClearable={false}
            searchable
            hideSelectedOptions
            isDisabled={isLegacyStudy || isReview}
          />
          {isDataFetched && !selectedDataRef.current.siteAckTemplateFsiId && (
            <div className="error justify-content-start">{translate('REQUIRED_PROPERTIES')}</div>
          )}
        </div>
        {!isReview && isAdmin && (
          <a className="add-new-doc-link" onClick={handleAddNewAckDoc}>
            <i className="fa fa-plus add-button" aria-hidden="true"></i>
            {translate('ADD_NEW_ACK_BUTTON')}
          </a>
        )}
      </div>
    </div>
  )
}

export default AcknowledgementDocSelection
